// @flow
import React from 'react';

// Components
import Recaptcha from 'react-google-invisible-recaptcha';

// Utils
import { getMetaTagContentByName, isMobile } from 'utils/DOM';

// Constants
import { GTM_RECAPTCHA_EVENTS, GTM_API } from 'constants/recaptcha';
const { cancel } = GTM_API;

type Props = {
  setRecaptchaToken: string => void,
  languageCode: string,
  onResolveAction: () => void,
  gtmAction: (event: string, api: string, token: string) => void,
  gtmApi: string,
  order: number,
  onLoaded: () => void
};

const {
  HUMAN_NO_CAPTCHA_NEEDED,
  HUMAN_CAPTCHA_SUCCESS,
  OPEN_MODAL,
  CAPTCHA_FAILED,
  CLOSE_MODAL,
  RECAPTCHA_ERROR
} = GTM_RECAPTCHA_EVENTS;

class InvisibleRecaptcha extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.recaptcha = React.createRef();
    this.initRecaptchaListener = this.initRecaptchaListener.bind(this);
    this.onResolveRecaptcha = this.onResolveRecaptcha.bind(this);
    this.onErrorRecaptcha = this.onErrorRecaptcha.bind(this);
    this.onOpenModal = this.onOpenModal.bind(this);
    this.onCloseModal = this.onCloseModal.bind(this);
    this.execute = this.execute.bind(this);
    this.reset = this.reset.bind(this);
    this.detectActiveCaptcha = this.detectActiveCaptcha.bind(this);
    this.state = {
      isRecaptchaModalOpen: false,
      wasRecaptchaCompleted: false,
      siteKey: getMetaTagContentByName('xtime:recaptcha-key'),
      reMountRecaptcha: false,
      pendingExecution: false,
      modalAdjusted: false,
      reCaptchaAttempt: 0
    };
  }

  componentDidUpdate() {
    const { reMountRecaptcha, pendingExecution } = this.state;
    if (reMountRecaptcha) {
      /* eslint-disable react/no-did-update-set-state */
      this.setState({ reMountRecaptcha: false });
      /* eslint-disable react/no-did-update-set-state */
    } else {
      if (pendingExecution) {
        /* eslint-disable react/no-did-update-set-state */
        this.setState({ pendingExecution: false });
        /* eslint-disable react/no-did-update-set-state */
        this.initRecaptchaListener();
        this.execute();
      }
    }
  }

  execute() {
    const { siteKey } = this.state;
    const currentSiteKey = getMetaTagContentByName('xtime:recaptcha-key');

    if (siteKey === currentSiteKey) {
      this.recaptcha.current.execute();
    } else {
      // Needed for automation
      this.setState({
        siteKey: currentSiteKey,
        reMountRecaptcha: true,
        pendingExecution: true
      });
    }
  }

  detectActiveCaptcha() {
    const allCaptchaIframes = Array.from(
      document.getElementsByTagName('iframe')
    ).filter(i => i.src.indexOf('captcha/api2/bframe') > 0);
    const captchaContainer = allCaptchaIframes.map(
      captchaIframe => captchaIframe.parentElement.parentElement
    );
    const activeCaptcha = captchaContainer.filter(
      container => container.style.visibility === 'visible'
    );
    return activeCaptcha && activeCaptcha[0];
  }

  reset() {
    const { setRecaptchaToken } = this.props;
    this.recaptcha.current.reset();
    setRecaptchaToken('');
    this.setState({
      isRecaptchaModalOpen: false,
      wasRecaptchaCompleted: false
    });
  }

  getAllReCaptchaWrappers() {
    const iframeArrayList = document.getElementsByTagName('iframe');
    const reCaptchaArrayList = [...iframeArrayList].filter(el =>
      el.src.includes('recaptcha/api2/bframe')
    );
    return reCaptchaArrayList.map(i => i.parentElement.parentElement);
  }

  onOpenModal() {
    const { isRecaptchaModalOpen, modalAdjusted } = this.state;
    const { gtmAction, gtmApi } = this.props;
    const isCancel = gtmApi === cancel;
    if (isCancel) {
      const wrapper = this.detectActiveCaptcha();
      wrapper.style.top = isMobile()
        ? document.documentElement.scrollTop + 'px'
        : '120px';
      this.setState({
        modalAdjusted: true
      });
    }
    if (isRecaptchaModalOpen) {
      if (
        !this.recaptcha.current.getResponse() &&
        (modalAdjusted || !isCancel)
      ) {
        gtmAction(CAPTCHA_FAILED, gtmApi);
        if (isCancel) {
          this.setState({
            modalAdjusted: false
          });
        }
      }
    } else {
      gtmAction(OPEN_MODAL, gtmApi);
      if (isCancel) {
        this.setState({
          modalAdjusted: false
        });
      }
    }
    this.setState({
      isRecaptchaModalOpen: true
    });
  }

  onCloseModal() {
    const { gtmAction, gtmApi } = this.props;
    const { wasRecaptchaCompleted } = this.state;
    if (!wasRecaptchaCompleted) {
      gtmAction(CLOSE_MODAL, gtmApi);
    }
    this.setState({
      isRecaptchaModalOpen: false,
      wasRecaptchaOpenOnce: false
    });
  }

  initRecaptchaListener() {
    const observerOptions = {
      attributes: true,
      attributeFilter: ['style']
    };

    const allReCaptchaWrappers = this.getAllReCaptchaWrappers();
    allReCaptchaWrappers.forEach(recaptchaWrapper => {
      const openRecaptchaModalObserver = new MutationObserver(
        x => +recaptchaWrapper.style.opacity === 1 && this.onOpenModal()
      );

      const closedRecaptchaModalObserver = new MutationObserver(
        x =>
          +recaptchaWrapper.style.opacity === 0 &&
          this.state.isRecaptchaModalOpen &&
          this.onCloseModal()
      );

      if (recaptchaWrapper) {
        openRecaptchaModalObserver.observe(recaptchaWrapper, observerOptions);
        closedRecaptchaModalObserver.observe(recaptchaWrapper, observerOptions);
      } else {
        setTimeout(() => this.initRecaptchaListener(), 0);
      }
    });
  }

  onResolveRecaptcha(token) {
    const { gtmAction, gtmApi } = this.props;
    const { isRecaptchaModalOpen } = this.state;
    if (isRecaptchaModalOpen) {
      gtmAction(HUMAN_CAPTCHA_SUCCESS, gtmApi, token);
    } else {
      gtmAction(HUMAN_NO_CAPTCHA_NEEDED, gtmApi, token);
    }
    this.setState({
      wasRecaptchaCompleted: true
    });
  }

  onErrorRecaptcha() {
    const { gtmAction, gtmApi } = this.props;
    const { reCaptchaAttempt } = this.state;
    gtmAction(RECAPTCHA_ERROR, gtmApi);
    if (reCaptchaAttempt < 1) {
      this.setState({
        reCaptchaAttempt: reCaptchaAttempt + 1
      });
      setTimeout(() => this.recaptcha.current.execute(), 1000);
    }
  }

  render() {
    const {
      languageCode,
      setRecaptchaToken,
      onResolveAction,
      onLoaded
    } = this.props;
    const { siteKey, reMountRecaptcha } = this.state;

    return (
      languageCode &&
      !reMountRecaptcha && (
        <Recaptcha
          locale={languageCode}
          onExpired={() => {
            setRecaptchaToken('');
          }}
          onError={() => {
            this.onErrorRecaptcha();
          }}
          onResolved={async token => {
            await this.onResolveRecaptcha(token);
            setRecaptchaToken(token);
            await onResolveAction();
          }}
          ref={this.recaptcha}
          sitekey={siteKey}
          onLoaded={onLoaded}
        />
      )
    );
  }
}

InvisibleRecaptcha.defaultProps = {
  order: 1
};

export default InvisibleRecaptcha;



// WEBPACK FOOTER //
// ./src/components/molecules/InvisibleRecaptcha/InvisibleRecaptcha.js